/**
 * 订单号管理API服务
 * 提供订单号相关的API调用方法
 */

import { signRequest } from '../utils/apiSignature.js';
import { useAuthStore } from '../stores/auth.js';
import type {
  Order,
  CreateOrderInput,
  BatchCreateOrdersInput,
  UpdateOrderInput,
  OrderQueryParams,
  OrderStatistics,
  PaginatedResult,
  OrderValidationResult,
} from '../types/order';

/**
 * API响应格式
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * 订单号API服务类
 */
export class OrderApiService {
  private baseUrl: string;

  constructor() {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    this.baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  /**
   * 通用请求方法（带签名）
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';

    // 从 authStore 获取 token
    const token = useAuthStore.getState().accessToken;

    // 调试日志
    console.log('[Order API] 请求信息:', {
      endpoint,
      method,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'null',
    });

    // 解析请求体
    let body: any = undefined;
    if (options.body) {
      try {
        body = JSON.parse(options.body as string);
      } catch {
        body = options.body;
      }
    }

    // 构建请求头
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string>),
    };

    // 📝 所有请求都需要API签名(包括已认证的请求)
    // 签名提供额外的安全层,防止请求篡改和重放攻击
    const signatureHeaders = await signRequest(
      url,
      method,
      body,
      {} // query参数已经在url中
    );
    Object.assign(headers, signatureHeaders);

    const defaultOptions: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, defaultOptions);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData: ApiResponse = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // 如果无法解析错误响应，使用默认错误消息
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * 创建订单号
   */
  async createOrder(input: CreateOrderInput): Promise<Order> {
    const response: ApiResponse<{ order: Order }> = await this.request(
      '/api/orders',
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );

    if (!response.success || !response.data?.order) {
      throw new Error(response.error || '创建订单号失败');
    }

    return response.data.order;
  }

  /**
   * 批量创建订单号
   */
  async batchCreateOrders(input: BatchCreateOrdersInput): Promise<{
    success: Order[];
    failed: Array<{ order_no: string; error: string }>;
    summary: {
      total: number;
      success_count: number;
      failed_count: number;
    };
  }> {
    const response: ApiResponse<{
      success: Order[];
      failed: Array<{ order_no: string; error: string }>;
      summary: {
        total: number;
        success_count: number;
        failed_count: number;
      };
    }> = await this.request('/api/orders/batch', {
      method: 'POST',
      body: JSON.stringify(input),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || '批量创建订单号失败');
    }

    return response.data;
  }

  /**
   * 获取订单号列表
   */
  async getOrders(params?: OrderQueryParams): Promise<PaginatedResult<Order>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const endpoint = `/api/orders${queryString ? `?${queryString}` : ''}`;

    const response: ApiResponse<PaginatedResult<Order>> = await this.request(endpoint);

    if (!response.success || !response.data) {
      throw new Error(response.error || '获取订单号列表失败');
    }

    return response.data;
  }

  /**
   * 获取单个订单号详情
   */
  async getOrder(orderNo: string): Promise<{
    order: Order;
    logs: any[];
  }> {
    const response: ApiResponse<{
      order: Order;
      logs: any[];
    }> = await this.request(`/api/orders/${encodeURIComponent(orderNo)}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || '获取订单号详情失败');
    }

    return response.data;
  }

  /**
   * 更新订单号
   */
  async updateOrder(orderNo: string, input: UpdateOrderInput): Promise<Order> {
    const response: ApiResponse<{ order: Order }> = await this.request(
      `/api/orders/${encodeURIComponent(orderNo)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );

    if (!response.success || !response.data?.order) {
      throw new Error(response.error || '更新订单号失败');
    }

    return response.data.order;
  }

  /**
   * 删除订单号
   */
  async deleteOrder(orderNo: string): Promise<void> {
    const response: ApiResponse = await this.request(
      `/api/orders/${encodeURIComponent(orderNo)}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.success) {
      throw new Error(response.error || '删除订单号失败');
    }
  }

  /**
   * 获取订单号统计信息
   */
  async getStatistics(): Promise<OrderStatistics> {
    const response: ApiResponse<OrderStatistics> = await this.request(
      '/api/orders/statistics'
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || '获取统计信息失败');
    }

    return response.data;
  }

  /**
   * 验证订单号是否可用
   */
  async validateOrder(
    orderNo: string,
    productCode: string
  ): Promise<OrderValidationResult> {
    const response: ApiResponse<OrderValidationResult> = await this.request(
      '/api/orders/validate',
      {
        method: 'POST',
        body: JSON.stringify({
          order_no: orderNo,
          product_code: productCode,
        }),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || '验证订单号失败');
    }

    return response.data;
  }
}

// 导出单例
export const orderApiService = new OrderApiService();
